import classNames from "classnames";
import { useEffect, useId, useRef } from "react";
import { InputSwitch } from "primereact/inputswitch";
import styles from "./ToggleSwitch.module.css";

export interface ToggleSwitchProps {
  name: string;
  id?: string;
  label?: string;
  leftLabel?: string;
  checked?: boolean;
  onChange(arg0: any): void;
  disabled?: boolean;
  color?: string;
  noWrapLabel?: boolean;
}

export const ToggleSwitch = ({
  name,
  id,
  label,
  leftLabel,
  checked,
  onChange,
  disabled,
  color,
  noWrapLabel,
}: ToggleSwitchProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();

  const switchId = id || `${name}-${generatedId}`;
  const labelId = `${switchId}-label`;

  const primaryLabel = leftLabel || label;

  useEffect(() => {
    if (!containerRef.current) return;

    const inputSwitch =
      containerRef.current.querySelector<HTMLElement>(".p-inputswitch");
    if (!inputSwitch) return;

    const input = inputSwitch.querySelector<HTMLInputElement>(
      ".p-inputswitch-input",
    );
    if (!input) return;

    // CRITICAL: Prevent nested interactive controls (WCAG/EN 301 549 requirement)
    // The outer div should NOT be focusable - only the input element should be interactive
    inputSwitch.removeAttribute("tabindex");
    inputSwitch.tabIndex = -1;
    // Remove any role from outer div that would make it interactive
    inputSwitch.removeAttribute("role");

    // The input is the actual form control and should be the only focusable element
    if (disabled) {
      input.tabIndex = -1;
    } else {
      input.removeAttribute("tabindex");
    }

    input.setAttribute("role", "switch");

    input.setAttribute("aria-checked", checked ? "true" : "false");

    inputSwitch.setAttribute("data-checked", checked ? "true" : "false");
    inputSwitch.removeAttribute("aria-checked");

    if (disabled) {
      input.setAttribute("aria-disabled", "true");
    } else {
      input.removeAttribute("aria-disabled");
    }

    if (primaryLabel) {
      input.setAttribute("aria-labelledby", labelId);
      inputSwitch.removeAttribute("aria-labelledby");
      inputSwitch.removeAttribute("aria-label");
    } else {
      input.setAttribute("aria-label", name);
      inputSwitch.removeAttribute("aria-labelledby");
      inputSwitch.removeAttribute("aria-label");
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (disabled) return;
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        input.click();
      }
    };

    input.addEventListener("keydown", handleKeyDown);

    return () => {
      input.removeEventListener("keydown", handleKeyDown);
    };
  }, [checked, disabled, switchId, labelId, primaryLabel, name]);

  return (
    <div className={styles.toggleSwitchWrapper}>
      {leftLabel && (
        <label
          id={labelId}
          style={{
            marginRight: "8px",
            color,
            ...(noWrapLabel && { whiteSpace: "nowrap" }),
          }}
          className={styles.labelRowLabel}
          htmlFor={switchId}
        >
          {leftLabel}
        </label>
      )}
      <div
        ref={containerRef}
        className={classNames([styles.toggleSwitchContainer], {
          [styles.toggleSwitchDisabled]: disabled,
        })}
      >
        <InputSwitch
          name={name}
          inputId={switchId}
          checked={!!checked}
          disabled={disabled}
          onChange={onChange}
        />
      </div>
      {label && (
        <label
          id={leftLabel ? undefined : labelId}
          style={{
            marginLeft: "8px",
            color,
            ...(noWrapLabel && { whiteSpace: "nowrap" }),
          }}
          className={styles.labelRowLabel}
          htmlFor={switchId}
        >
          {label}
        </label>
      )}
    </div>
  );
};

export default ToggleSwitch;
